import React, { useEffect, useContext } from "react";
import {
  Route,
  Routes,
  useParams,
  useNavigate,
  useLocation,
} from "react-router-dom";
import IDMatch from "./pages/idmatch/coppa";
import Dcams from "./pages/dcams";
import Location from "./pages/idmatch/predict/location";
import AgeMatchModular from "./pages/agematch/modular";
import AgeEstimation from "./pages/agematch/estimation";
import YotiButton from "./pages/digital-id/yoti";
import YotiCallback from "./pages/digital-id/yoti/callback";
import Handoff from "./pages/handoff";
import NotFound from "./pages/not-found";
import { DataContext } from "./context/DataContext";
import DcamsModular from "./pages/dcams/modular";
import Clear from "./pages/dcams/clear";
import AgeMatch from "./pages/agematch/process";
import PhoneMatch from "./pages/phonematch";
import ErrorMessage from "./components/error-message";
import Plaid from "./pages/dcams/plaid/plaid-start";
import CaMdl from "./pages/dcams/mdl/ca";
import YotiID from "./pages/dcams/yoti";
import LAWallet from "./pages/dcams/la-wallet";
import OneIdStart from "./pages/dcams/one-id/start";
import OneIdReturn from "./pages/dcams/one-id/return";
import DIGILOCKERStart from "./pages/dcams/digilocker/start";
import DigiLockerReturn from "./pages/dcams/digilocker/return";
import IDMatchPlus from "./pages/idmatch/plus";
import { LayerPlacement } from "maplibre-gl";
import CaMdlReturn from "./pages/dcams/mdl/ca/return";
import TrinsicReturn from "./pages/dcams/trinsic/TrinsicReturn";
import BrazilCpfStart from "./pages/dcams/brazil-cpf/start";
import BankIdSeStart from "./pages/dcams/bankid-se/start";
import BankIdNoStart from "./pages/dcams/bankid-no/start";
import VeratadFaceCapture from "./pages/face-capture/veratad";
import VeratadIdDocCapture from "./pages/id-doc/veratad-capture";
import RjSsnVerification from "./pages/rj/ssn/verification";
import VerificationTransition from "./components/VerificationTransition";
import SuppliedImagesPending from "./components/supplied-images-pending";
import { areSuppliedImagesPending } from "./utils/suppliedImages";
const componentMap = {
  handoff: Handoff,
  "idmatch/coppa": IDMatch,
  "agematch/modular": AgeMatchModular,
  "agematch/estimate": AgeEstimation,
  "agematch/process": AgeMatch,
  phonematch: PhoneMatch,
  "dcams/mitek": Dcams,
  "dcams/modular": DcamsModular,
  "face-capture/:sdk": VeratadFaceCapture,
  "idmatch/predict/location": Location,
  "yoti/id/callback": YotiCallback,
  "dcams/clear/clear-start": Clear,
  "dcams/plaid/plaid-start": Plaid,
  "dcams/yoti": YotiID,
  "dcams/la-wallet": LAWallet,
  "dcams/mdl/ca": CaMdl,
  "dcams/oneid": OneIdStart,
  "dcams/digilocker": DIGILOCKERStart,
  "digilocker/return/success": DigiLockerReturn,
  "digilocker/return/failure": DigiLockerReturn,
  "oneid/return": OneIdReturn,
  "clear/return": Clear,
  "idmatch/plus": IDMatchPlus,
  "dcams/brazil-cpf": BrazilCpfStart,
  "dcams/bankid-se": BankIdSeStart,
  "dcams/bankid-no": BankIdNoStart,
  "face-capture/veratad": VeratadFaceCapture,
  "id-doc/veratad-capture": VeratadIdDocCapture,
  "idmatch/rj/ssn/verification": RjSsnVerification,
  // BlinkID in-browser capture renders through the Dcams page so it gets
  // the same intro screen, instructions, translations, and handoff flow
  // as the other document-capture providers.
  "dcams/blinkid": Dcams,
};

const SubRoutes = () => {
  const { currentIndex, routeSequence, settings, selectedComponent, showTransition, dispatch } =
    useContext(DataContext);
  const navigate = useNavigate();
  const location = useLocation();

  const isModular = settings.journey.settings?.isModular || false;

  // Log the current index and route sequence for debugging
  //console.log("Current index:", currentIndex);
  //console.log("Route sequence:", routeSequence);
  //console.log("Selected Component:", selectedComponent);

  // Get the current path from routeSequence using currentIndex
  /*
  const currentPath =
    isModular && routeSequence.length > 1
      ? selectedComponent
      : routeSequence[currentIndex]?.path;
      */
  const currentPath = routeSequence[currentIndex]?.path;
  // console.log("Current path:", currentPath);

  if (location.pathname.includes("/mdl/ca/return")) {
    //console.log("Navigating to CAMDL");
    return (
      <Routes>
        <Route path="*" element={<CaMdlReturn />} />
      </Routes>
    );
  }

  if (location.pathname.includes("/clear/return")) {
    //console.log("Navigating to Clear Return");
    return (
      <Routes>
        <Route path="*" element={<Clear />} />
      </Routes>
    );
  }

  if (location.pathname.includes("/oneid/return")) {
    //console.log("Navigating to OneId Return");
    return (
      <Routes>
        <Route path="*" element={<OneIdReturn />} />
      </Routes>
    );
  }

  if (location.pathname.includes("/digilocker/return")) {
    //console.log("Navigating to DigiLocker Return");
    return (
      <Routes>
        <Route path="*" element={<DigiLockerReturn />} />
      </Routes>
    );
  }

  // Consolidated Trinsic return route (handles bankid-cz, bankid-se, bankid-no, finnish-trust-network, mojeid)
  if (location.pathname.includes("/trinsic/return") ||
      location.pathname.includes("/bankid-cz/return") ||
      location.pathname.includes("/bankid-se/return") ||
      location.pathname.includes("/bankid-no/return") ||
      location.pathname.includes("/finnish-trust-network/return") ||
      location.pathname.includes("/mojeid/return")) {
    return (
      <Routes>
        <Route path="*" element={<TrinsicReturn />} />
      </Routes>
    );
  }

  if (isModular && !currentPath && routeSequence.length > 1) {
    //console.log("it is modular");
    //return <AgeMatchModular />;
  }

  // Journey API prefill: the publisher declared images but has not
  // confirmed the upload. Hold every step (ahead of its handoff and
  // device guards) until get_session reports images_received_at.
  if (areSuppliedImagesPending(settings)) {
    return (
      <Routes>
        <Route path="*" element={<SuppliedImagesPending />} />
      </Routes>
    );
  }

  // Show verification transition screen between steps when configured
  if (showTransition) {
    const handleTransitionContinue = () => {
      dispatch({ type: "SET_SHOW_TRANSITION", payload: false });
      dispatch({ type: "INCREMENT_COMPLETED_ROUTES" });
      // Clear the previous step's terminal flags so the next
      // component mounts fresh — otherwise `navigateToNext` could
      // re-fire from stale `isCurrentComponentComplete: true` and
      // bounce the user back to the transition screen.
      dispatch({ type: "SET_IS_CURRENT_COMPONENT_COMPLETE", payload: false });
      const nextIndex = currentIndex + 1;
      const nextRoute = routeSequence[nextIndex];
      if (nextRoute) {
        dispatch({ type: "SET_CURRENT_INDEX", payload: nextIndex });
        dispatch({ type: "SET_STATUS", payload: "PENDING" });
        const currentSearch = window.location.search || '';
        navigate(nextRoute.path + currentSearch);
      }
    };
    return (
      <Routes>
        <Route path="*" element={<VerificationTransition onContinue={handleTransitionContinue} />} />
      </Routes>
    );
  }

  // Find the corresponding component from the mapping
  const CurrentComponent = componentMap[currentPath] || NotFound;
  //console.log("Current Component:", CurrentComponent.name);

  return (
    <Routes>
      <Route path="*" element={<CurrentComponent />} />
    </Routes>
  );
};

export default SubRoutes;
